<script setup lang="ts">
import { api } from '@/api';
import {
  type CreateUpdateEnumerationQuantityDto,
  EntityOrigin,
  EntityWithCommentType,
  type EnumerationQuantityDto,
  ReferencingObjectDto,
} from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import type { EntityInfo } from '@/components/confirmationModals/types';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import VeeInputRows from '@/components/forms/InputRows/VeeInputRows.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import HistoryEvents from '@/components/history/HistoryEvents.vue';
import { mainNavigationQaEquipment, mainNavigationMedicalSystems } from '@/components/icons/icons';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { useScopeOptions } from '@/components/quantitiesAndUnits/quantitiesAndUnitsUtils';
import { type TabDefinition } from '@/components/Tabs/types.ts';
import { routenames } from '@/router/routenames';
import { translateEntityProperty, type TranslatableEntity } from '@/utils/helper.ts';
import GreenBoxHolder from '@/views/quantities/components/GreenBoxHolder.vue';
import { useTranslation } from 'i18next-vue';
import { useFormValues, useFieldArray } from 'vee-validate';
import { computed, onMounted, ref, toRef } from 'vue';
import { useRouter } from 'vue-router';
import { useDeleteEnumerationWithConfirmation } from '../utils';
import ScopeGroups from './ScopeGroups.vue';
import ScopeList from './ScopeList.vue';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import { useAmountComments } from '@/components/comments/useAmountComments';
import { useFormTab } from '@/components/forms/useFormTab';

const { t } = useTranslation();

type Enumeration = EnumerationQuantityDto & CreateUpdateEnumerationQuantityDto;

const values = useFormValues<Enumeration>();

const mode = useFormMode();
const { action: deleteAction } = useDeleteEnumerationWithConfirmation();

const initialUseInSetup = Boolean(values.value?.setupUsage?.isUsageEnabled);
const initialUseInEvaluations = Boolean(values.value?.evaluationUsage?.isUsageEnabled);

const isSetupUsageEnabled = computed(() => values.value.setupUsage?.isUsageEnabled ?? false);
const isEvaluationsUsageEnabled = computed(
  () => values.value.evaluationUsage?.isUsageEnabled ?? false,
);

const { commentsChanged, commentsTabTitle } = useAmountComments(values);

const isPredefined = computed(() => values.value.origin === EntityOrigin.Predefined);
const isInEditMode = mode.value === 'edit';
const isInAddMode = mode.value === 'add';
const isInDetailsMode = mode.value === 'details';

const isEditable = computed(() => {
  return {
    forSetup: isSetupUsageEnabled.value
      ? isInAddMode || (isInEditMode && !isPredefined.value && !values.value.isReferenced)
      : false,
    forEvaluations: isEvaluationsUsageEnabled.value
      ? isInAddMode || (isInEditMode && !isPredefined.value && !values.value.isReferenced)
      : false,
  };
});

const isDeletable = computed(() => {
  return {
    forSetup: isSetupUsageEnabled.value
      ? isInAddMode || (isInEditMode && !isPredefined.value && !values.value.isReferenced)
      : false,
    forEvaluations: isEvaluationsUsageEnabled.value
      ? isInAddMode || (isInEditMode && !isPredefined.value && !values.value.isReferenced)
      : false,
  };
});

const isReferenced = computed(() => (values.value as EnumerationQuantityDto).isReferenced || false);
const isDisabled = computed(() => {
  return {
    forSetup: !isSetupUsageEnabled.value && (isInEditMode || isInAddMode),
    forEvaluations: !isEvaluationsUsageEnabled.value && (isInEditMode || isInAddMode),
  };
});

const { fields: setupEnumerationFields } = useFieldArray<TranslatableEntity>(
  toRef(() => 'setupUsage.enumerationOptions'),
);

const { getIndicatorState } = useFormTab();

const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
        title: t('AdministrativeInformation'),
        indicatorState: getIndicatorState(['name', 'description', 'attachments']),
      },
      useInEvaluations: {
        title: t('UseInEvaluation_other'),
        hideContentSheet: true,
        indicatorState: getIndicatorState(['evaluationUsage']),
      },
      useInSetups: {
        title: t('UseInSetup_other'),
        hideContentSheet: true,
        indicatorState: getIndicatorState(['setupUsage']),
      },
      comments: {
        ...defaultPageTabs.comments,
        hidden: isInAddMode,
        title: commentsTabTitle.value,
      },

      history: {
        ...defaultPageTabs.history,
        hidden: defaultPageTabs.history.hidden || isInAddMode,
        unmountTab: true,
      },
    }) satisfies TabDefinition,
);

const router = useRouter();

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);

  return [
    {
      ...defaultPageActions.edit,
      to: {
        name: routenames.enumerations.edit,
        params: { id: values.value?.id },
      },
    },
    {
      ...defaultPageActions.delete,
      callback: async () => {
        if (values.value.id) {
          const { result } = await deleteAction({
            param: values.value.id,
            confirmationOptions: getDefaultDeleteConfirmationOptions(
              {
                ...(values.value as EntityInfo),
                typeName: 'EnumerationItem_one',
              },
              t,
            ),
          });
          if (result?.success) {
            router.push({
              name: routenames.enumerations.overview,
            });
          }
        }
      },
    },
  ];
});

const entityReferences = ref<ReferencingObjectDto[] | null>(null);

const { load, options } = useScopeOptions();

onMounted(load);
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <!-- ADMINISTRATIVE -->
    <template #administrative>
      <FormSection>
        <v-row class="justify-sm-start">
          <v-col cols="5">
            <VeeTextField
              :label="$t('Name')"
              name="name"
              :mode="isPredefined ? 'details' : undefined"
              :placeholder="$t('Required')"
              :outputConverter="() => translateEntityProperty('name', values)"
            />
          </v-col>

          <v-col cols="5">
            <VeeTextarea
              :label="$t('Description')"
              name="description"
              :mode="isPredefined ? 'details' : undefined"
              :outputConverter="() => translateEntityProperty('description', values)"
            />
            <VeeAttachments class="mt-1" name="attachments" data-testid="Description_Attachments" />
          </v-col>

          <v-col cols="2">
            <VeeAvailability :disabled="mode !== 'details'" name="isAvailable" />
            <EntityChangeBlock v-if="isInDetailsMode" :entity="values" />
          </v-col>
        </v-row>
      </FormSection>
    </template>

    <!-- USEINEVALUATIONS -->
    <template #useInEvaluations>
      <div class="d-flex flex-column ga-6">
        <InlineNotification
          v-if="(isReferenced || isPredefined) && isInEditMode"
          variant="flat"
          class="mb-4"
          :value="{
            type: 'info',
            message: $t('FieldsCanNotBeEditedBecauseOfReferenceOfEntityMsg', {
              entity: $t('Enumeration_one'),
            }),
          }"
        >
          <template #append>
            <span
              v-if="values.isReferenced"
              class="ml-2 linkButton"
              @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
              >{{ $t('Info') }}</span
            >
          </template>
        </InlineNotification>

        <GreenBoxHolder v-if="!isInDetailsMode" :active="!!values?.evaluationUsage?.isUsageEnabled">
          <VeeCheckbox
            :hideDetails="true"
            :noValidationOnCheck="true"
            :disabled="isInEditMode && initialUseInEvaluations && (isReferenced || isPredefined)"
            name="evaluationUsage.isUsageEnabled"
            :label="$t('UseInEvaluation_other')"
          />
        </GreenBoxHolder>

        <template v-if="!values?.evaluationUsage?.isUsageEnabled && isInDetailsMode">
          <p class="text-body-1 font-weight-bold text-primary-lighten-2">
            {{ $t('EnumerationNotUsedInEvaluationsMsg') }}
          </p>
        </template>

        <FormSection
          :disabled="!values?.evaluationUsage?.isUsageEnabled"
          data-testid="formSection_Enumeration"
        >
          <v-card class="container-level-1">
            <v-card-item>
              <v-card-title>
                <h5>{{ $t('EnumerationItem_other') }}</h5>
              </v-card-title>
            </v-card-item>
            <v-card-text>
              <template v-if="isInDetailsMode">
                <template v-if="values.evaluationUsage?.enumerationOptions?.length">
                  <v-chip
                    v-for="field in values.evaluationUsage?.enumerationOptions"
                    :key="field.id!"
                    class="mr-2 mb-2"
                  >
                    {{ translateEntityProperty('name', field as TranslatableEntity) }}
                  </v-chip>
                </template>
                <span v-else>{{ EMPTY_FIELD_SIGN }}</span>
              </template>
              <VeeInputRows
                v-else
                class="input-field-max-width"
                name="evaluationUsage.enumerationOptions"
                :disabled="isDisabled.forEvaluations"
                :isEditable="isEditable.forEvaluations"
                :isDeletable="isDeletable.forEvaluations"
                :isExtendable="!isDisabled.forEvaluations"
                :deleteTooltipText="$t('DeleteEnumerationItem')"
                :addTooltipText="$t('AddEnumerationItem')"
              />
            </v-card-text>
          </v-card>
        </FormSection>

        <FormSection
          :disabled="!values?.evaluationUsage?.isUsageEnabled"
          data-testid="formSection_UseInTaskCategory"
        >
          <v-card class="container-level-1">
            <v-card-item
              ><v-card-title>
                <h5>{{ $t('UseInTaskCategory_other') }}</h5>
              </v-card-title>
            </v-card-item>
            <v-card-text>
              <v-row>
                <v-col cols="6">
                  <v-label>
                    <v-icon
                      size="40"
                      class="mr-2 text-primary-darken-2"
                      :icon="mainNavigationMedicalSystems"
                    />
                    <h6 class="text-primary-darken-2">
                      {{ $t('MedicalSystems') }}
                    </h6>
                  </v-label>
                  <ScopeList
                    name="evaluationUsage.medicalSystemScope"
                    :scopeOptions="options.medicalSystems"
                    lastItemKey="OtherMedicalSystem"
                    scopeKey="rtSystemCategory"
                    :disabled="isDisabled.forEvaluations"
                    :disableSettedOptions="
                      (isReferenced || isPredefined) && values.evaluationUsage?.isUsageEnabled
                    "
                  />
                </v-col>
                <v-col cols="6">
                  <v-label>
                    <v-icon
                      size="40"
                      class="mr-2 text-primary-darken-2"
                      :icon="mainNavigationQaEquipment"
                    />
                    <h6 class="text-primary-darken-2">
                      {{ $t('DosimetryAndQaEquipment') }}
                    </h6>
                  </v-label>

                  <ScopeList
                    name="evaluationUsage.qaEquipmentScope"
                    :scopeOptions="options.qaEquipment"
                    scopeKey="rtSystemSubCategory"
                    :disabled="isDisabled.forEvaluations"
                    :disableSettedOptions="
                      (isReferenced || isPredefined) && values.evaluationUsage?.isUsageEnabled
                    "
                  />
                </v-col> </v-row
            ></v-card-text>
          </v-card>
        </FormSection>
      </div>
    </template>

    <!-- SETUPS -->
    <template #useInSetups>
      <div class="d-flex flex-column ga-6">
        <InlineNotification
          v-if="(isReferenced || isPredefined) && isInEditMode"
          variant="flat"
          :value="{
            type: 'info',
            message: $t('FieldsCanNotBeEditedBecauseOfReferenceOfEntityMsg', {
              entity: $t('Enumeration_one'),
            }),
          }"
        >
          <template #append>
            <span
              v-if="values.isReferenced"
              class="ml-2 linkButton"
              @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
              >{{ $t('Info') }}</span
            >
          </template>
        </InlineNotification>

        <GreenBoxHolder v-if="!isInDetailsMode" :active="!!values?.setupUsage?.isUsageEnabled">
          <VeeCheckbox
            :hideDetails="true"
            :noValidationOnCheck="true"
            :disabled="isInEditMode && initialUseInSetup && (isReferenced || isPredefined)"
            name="setupUsage.isUsageEnabled"
            :label="$t('UseInSetup_other')"
          />
        </GreenBoxHolder>

        <template v-if="!values?.setupUsage?.isUsageEnabled && isInDetailsMode">
          <p class="text-body-1 font-weight-bold text-primary-lighten-2">
            {{ $t('EnumerationNotUsedInSetupsMsg') }}
          </p>
        </template>

        <template v-else>
          <FormSection
            data-testid="formSection_Enumeration"
            :disabled="!values.setupUsage?.isUsageEnabled"
          >
            <v-card class="container-level-1">
              <v-card-item>
                <v-card-title>
                  <h5>{{ $t('EnumerationItem_other') }}</h5>
                </v-card-title>
              </v-card-item>
              <v-card-text>
                <template v-if="isInDetailsMode">
                  <template v-if="setupEnumerationFields?.length">
                    <v-chip
                      v-for="field in setupEnumerationFields"
                      :key="field.key"
                      class="mr-2 mb-2"
                    >
                      {{ translateEntityProperty('name', field.value as TranslatableEntity) }}
                    </v-chip>
                  </template>
                  <span v-else>{{ EMPTY_FIELD_SIGN }}</span>
                </template>
                <VeeInputRows
                  v-else
                  class="input-field-max-width"
                  name="setupUsage.enumerationOptions"
                  :deleteTooltipText="$t('DeleteEnumerationItem')"
                  :addTooltipText="$t('AddEnumerationItem')"
                  :disabled="isDisabled.forSetup"
                  :isEditable="isEditable.forSetup"
                  :isDeletable="isDeletable.forSetup"
                  :isExtendable="!isDisabled.forSetup"
                />
              </v-card-text>
            </v-card>
          </FormSection>

          <FormSection
            :disabled="!values.setupUsage?.isUsageEnabled"
            data-testid="formSection_UseInSetupCategory"
          >
            <v-card class="container-level-1">
              <v-card-item
                ><v-card-title>
                  <h5>{{ $t('UseInSetupCategory_other') }}</h5>
                </v-card-title></v-card-item
              >
              <v-card-text>
                <v-row>
                  <v-col cols="12">
                    <v-label>
                      <v-icon
                        size="40"
                        class="mr-2 text-primary-darken-2"
                        :icon="mainNavigationMedicalSystems"
                      />
                      <h6 class="text-primary-darken-2">
                        {{ $t('MedicalSystems') }}
                      </h6>
                    </v-label>
                    <ScopeGroups
                      name="setupUsage.medicalSystemScope"
                      :scopeOptions="options.medicalSystems"
                      scopeKey="rtSystemSubCategory"
                      groupKey="rtSystemCategory"
                      :disableSettedOptions="
                        (isReferenced || isPredefined) && values.setupUsage?.isUsageEnabled
                      "
                      :disabled="isDisabled.forSetup"
                    />
                  </v-col>
                </v-row>
                <v-divider class="my-6" />
                <v-row>
                  <v-col cols="12">
                    <v-label>
                      <v-icon
                        size="40"
                        class="mr-2 text-primary-darken-2"
                        :icon="mainNavigationQaEquipment"
                      />
                      <h6 class="text-primary-darken-2">
                        {{ $t('DosimetryAndQaEquipment') }}
                      </h6>
                    </v-label>
                    <ScopeList
                      name="setupUsage.qaEquipmentScope"
                      :scopeOptions="options.qaEquipment"
                      scopeKey="rtSystemSubCategory"
                      :disableSettedOptions="
                        (isReferenced || isPredefined) && values.setupUsage?.isUsageEnabled
                      "
                      :disabled="isDisabled.forSetup"
                    />
                  </v-col> </v-row
              ></v-card-text>
            </v-card>
          </FormSection>
        </template>
      </div>
    </template>

    <template #comments>
      <FormSection data-testid="formSection_Comments">
        <Comments
          v-if="values.id"
          :id="values.id"
          :entityWithCommentType="EntityWithCommentType.Enumeration"
          @change="commentsChanged"
        />
      </FormSection>
    </template>

    <template #history>
      <HistoryEvents
        v-if="values.id"
        :id="values.id"
        :historyApiFn="
          api.enumerationQuantitiesApi.getEnumerationHistory.bind(api.enumerationQuantitiesApi)
        "
      />
    </template>
  </BasePageNavigation>
  <EntityReferencesModal
    v-if="values.isReferenced"
    :references="entityReferences"
    :title="$t('ReferenceingEntities')"
    @dialogOpen="
      (isOpen: boolean) => {
        if (!isOpen) entityReferences = null;
      }
    "
  ></EntityReferencesModal>
</template>
